/**
 * IptcConfigStep - Step 3 of match creation wizard
 *
 * Configures IPTC metadata templates for the match:
 * - Template fields with variable insertion
 * - Live preview of rendered values (using real match data from Step 1)
 * - Location fields (pre-filled from Step 1)
 * - Keywords input
 * - Category and Supplemental Categories
 * - Load from company defaults button
 * - Auto-prefill from match data (step 1)
 * - Auto-generated title in HOME_AWAY_CODE_DDMMYY format
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import TagInput from '../../../../shared/components/ui/TagInput/TagInput';
import Button from '../../../../shared/components/ui/Button';
import { COMPETITIONS, MONTHS } from '../../utils/constants';
import styles from './MatchModal.module.css';

// Available template variables with descriptions
const TEMPLATE_VARIABLES = [
  { name: 'home_team', label: 'Home Team', example: 'Feyenoord Rotterdam' },
  { name: 'away_team', label: 'Away Team', example: 'AFC Ajax' },
  { name: 'event', label: 'Event Name', example: 'Feyenoord vs Ajax' },
  { name: 'competition', label: 'Competition', example: 'Eredivisie' },
  { name: 'venue', label: 'Venue', example: 'Stadion Feijenoord' },
  { name: 'city', label: 'City', example: 'Rotterdam' },
  { name: 'country', label: 'Country', example: 'Netherlands' },
  { name: 'day', label: 'Day', example: '26' },
  { name: 'month', label: 'Month (numeric)', example: '01' },
  { name: 'monthname', label: 'Month Name', example: 'January' },
  { name: 'year4', label: 'Year', example: '2026' },
  { name: 'photog', label: 'Photographer', example: 'John Doe' },
  { name: 'persons', label: 'Tagged Persons', example: 'Player 1, Player 2' },
  { name: 'match_phase', label: 'Match Phase', example: 'during' },
  { name: 'copyright', label: 'Copyright', example: 'VK Sportphoto' },
  { name: 'company', label: 'Company', example: 'VK Sportphoto' },
];

// Preview field display labels
const PREVIEW_LABELS = {
  title_template: 'Title',
  headline_template: 'Headline',
  caption_template: 'Caption',
  event_name: 'Event Name',
  keywords: 'Keywords',
  credit: 'Credit',
  copyright_notice: 'Copyright',
  category: 'Category',
  supplemental_categories: 'Suppl. Categories',
  country_code: 'Country Code',
};

/**
 * Variable dropdown for inserting template variables
 */
function VariableDropdown({ onInsert }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={styles.variableDropdown}>
      <button
        type="button"
        className={styles.variableButton}
        onClick={() => setIsOpen(!isOpen)}
      >
        + Variable
      </button>
      {isOpen && (
        <div className={styles.variableMenu}>
          {TEMPLATE_VARIABLES.map((v) => (
            <button
              key={v.name}
              type="button"
              className={styles.variableItem}
              onClick={() => {
                onInsert(`{${v.name}}`);
                setIsOpen(false);
              }}
            >
              <span className={styles.variableName}>{`{${v.name}}`}</span>
              <span className={styles.variableLabel}>{v.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Template input with variable insertion dropdown
 */
function TemplateInput({ label, value, onChange, placeholder, multiline = false }) {
  const handleInsert = (variable) => {
    onChange(value + variable);
  };

  const InputComponent = multiline ? 'textarea' : 'input';

  return (
    <div className={styles.formGroup}>
      <div className={styles.templateHeader}>
        <label className={styles.label}>{label}</label>
        <VariableDropdown onInsert={handleInsert} />
      </div>
      <InputComponent
        type={multiline ? undefined : 'text'}
        className={multiline ? styles.textarea : styles.input}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={multiline ? 3 : undefined}
      />
    </div>
  );
}

/**
 * Build preview context from real match data for template substitution
 */
function buildPreviewContext(matchData, teams) {
  const homeTeam = teams?.find((t) => String(t.id) === String(matchData?.home_team));
  const awayTeam = teams?.find((t) => String(t.id) === String(matchData?.away_team));
  const date = matchData?.start_time ? new Date(matchData.start_time) : null;

  return {
    home_team: homeTeam?.name || '',
    away_team: awayTeam?.name || '',
    event: homeTeam && awayTeam ? `${homeTeam.name} vs ${awayTeam.name}` : '',
    competition: COMPETITIONS[matchData?.competition]?.label || '',
    venue: matchData?.venue || '',
    city: matchData?.city || '',
    state: matchData?.state || '',
    country: matchData?.country || '',
    day: date ? String(date.getDate()) : '',
    month: date ? String(date.getMonth() + 1).padStart(2, '0') : '',
    monthname: date ? MONTHS[date.getMonth()] : '',
    year4: date ? String(date.getFullYear()) : '',
    match_phase: 'during',
    // These stay as examples since we don't know them yet
    persons: 'Santiago Gimenez, Quinten Timber',
    photog: 'John Doe',
    copyright: 'VK Sportphoto',
    company: 'VK Sportphoto',
  };
}

/**
 * Live preview of rendered templates and static fields
 */
function PreviewPanel({ preview, config }) {
  // Build full preview merging template preview + static fields
  const fullPreview = {};

  // Add rendered template fields from API preview
  if (preview) {
    Object.assign(fullPreview, preview);
  }

  // Add static fields from config
  if (config) {
    if (config.event_name) fullPreview.event_name = config.event_name;
    if (config.keywords?.length > 0) fullPreview.keywords = config.keywords.join(', ');
    if (config.credit) fullPreview.credit = config.credit;
    if (config.copyright_notice) fullPreview.copyright_notice = config.copyright_notice;
    if (config.category) fullPreview.category = config.category;
    if (config.supplemental_categories?.length > 0) {
      fullPreview.supplemental_categories = config.supplemental_categories.join(', ');
    }
    if (config.country_code) fullPreview.country_code = config.country_code;
  }

  const entries = Object.entries(fullPreview).filter(([, value]) => value);

  if (entries.length === 0) {
    return (
      <div className={styles.previewPanel}>
        <div className={styles.previewHeader}>Preview</div>
        <p className={styles.previewEmpty}>Enter template values to see preview</p>
      </div>
    );
  }

  return (
    <div className={styles.previewPanel}>
      <div className={styles.previewHeader}>Preview</div>
      <div className={styles.previewContent}>
        {entries.map(([key, value]) => (
          <div key={key} className={styles.previewItem}>
            <span className={styles.previewLabel}>
              {PREVIEW_LABELS[key] || key.replace(/_template$/, '').replace(/_/g, ' ')}:
            </span>
            <span className={styles.previewValue}>{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Generate pre-fill IPTC values from match data
 * Follows the VK Sportphoto XMP template format
 *
 * @param {Object} matchData - Form data from step 1
 * @param {Array} teams - Available teams list
 * @returns {Object} Pre-filled IPTC config values
 */
function generateIptcFromMatchData(matchData, teams) {
  if (!matchData) return null;

  // Helper to get team by ID
  const getTeam = (teamId) => {
    if (!teamId || !teams) return null;
    return teams.find((t) => String(t.id) === String(teamId)) || null;
  };

  // Helper to get short team name (first word, e.g., "PSV" from "PSV Eindhoven")
  const getShortName = (fullName) => {
    if (!fullName) return null;
    return fullName.split(' ')[0].toLowerCase();
  };

  const homeTeam = getTeam(matchData.home_team);
  const awayTeam = getTeam(matchData.away_team);
  const homeTeamName = homeTeam?.name || null;
  const awayTeamName = awayTeam?.name || null;
  const homeShort = getShortName(homeTeamName);

  const competitionLabel = matchData.competition
    ? COMPETITIONS[matchData.competition]?.label || matchData.competition
    : null;
  const competitionLower = competitionLabel?.toLowerCase() || null;
  const competitionShortCode = matchData.competition
    ? COMPETITIONS[matchData.competition]?.shortCode || ''
    : '';

  // Calculate season (football: Aug-May means Jan-Jul is previous season)
  let seasonStr = null;
  if (matchData.start_time) {
    const matchDate = new Date(matchData.start_time);
    const year = matchDate.getFullYear();
    const month = matchDate.getMonth();
    const seasonStart = month < 7 ? year - 1 : year;
    const seasonEnd = seasonStart + 1;
    seasonStr = `${seasonStart}/${seasonEnd}`;
  }

  // Build keywords array (all lowercase, matching XMP format)
  const keywords = [];

  // Static keywords
  keywords.push('wedstrijd');
  keywords.push('action');
  keywords.push('duel');

  // Season keywords
  if (seasonStr) {
    keywords.push(`season ${seasonStr}`);
    if (competitionLower) {
      keywords.push(`${competitionLower} ${seasonStr}`);
    }
    keywords.push(seasonStr);
  }

  // Competition keyword
  if (competitionLower) {
    keywords.push(competitionLower);
  }

  // Sport keywords (for football)
  if (matchData.sport === 'football' || !matchData.sport) {
    keywords.push('soccer');
    keywords.push('sport');
    keywords.push('football');
  }

  // Team combination keywords (all lowercase)
  if (homeTeamName && awayTeamName) {
    const homeLower = homeTeamName.toLowerCase();
    const awayLower = awayTeamName.toLowerCase();
    keywords.push(`${homeLower} v ${awayLower}`);
    keywords.push(`${homeShort} - ${awayLower}`);
    keywords.push(homeLower);
    keywords.push(awayLower);
  }

  // Build headline: "Competition: Home Team v Away Team"
  let headline = '';
  if (competitionLabel && homeTeamName && awayTeamName) {
    headline = `${competitionLabel}: ${homeTeamName} v ${awayTeamName}`;
  } else if (homeTeamName && awayTeamName) {
    headline = `${homeTeamName} v ${awayTeamName}`;
  }

  // Build caption template with variables (for Photo Mechanic style)
  const caption = `{city} - {persons} {match_phase} the ${seasonStr || '{season}'} ${competitionLabel || '{competition}'} season. The match is set between {event} at {location} on {day} {monthname} {year4} in {city}, {country}.
({copyright}/{photog})`;

  // Build event name: "Home Team and Away Team"
  let eventName = '';
  if (homeTeamName && awayTeamName) {
    eventName = `${homeTeamName} and ${awayTeamName}`;
  }

  // Build title (dc:title) - Object Name format: HOME TEAM_AWAY TEAM_CODE_DDMMYY
  let title = '';
  if (homeTeamName && awayTeamName) {
    let dateStr = '';
    if (matchData.start_time) {
      const matchDate = new Date(matchData.start_time);
      const dd = String(matchDate.getDate()).padStart(2, '0');
      const mm = String(matchDate.getMonth() + 1).padStart(2, '0');
      const yy = String(matchDate.getFullYear()).slice(-2);
      dateStr = `${dd}${mm}${yy}`;
    }
    const parts = [homeTeamName.toUpperCase(), awayTeamName.toUpperCase()];
    if (competitionShortCode) parts.push(competitionShortCode);
    if (dateStr) parts.push(dateStr);
    title = parts.join('_');
  }

  // Use location data from matchData (entered in Step 1)
  return {
    title_template: title,
    keywords,
    headline_template: headline,
    caption_template: caption,
    event_name: eventName,
    // Location fields from Step 1
    location: matchData.venue || '',
    city: matchData.city || '',
    state: matchData.state || '',
    country: matchData.country || '',
    country_code: matchData.country_code || '',
    // Credit (PM variable, substituted per-photo at export)
    credit: '{photog}',
    // Category defaults
    category: 'S',
    supplemental_categories: ['SPO', 'SOC', 'FOC'],
  };
}

/**
 * IptcConfigStep component
 *
 * @param {Object} props
 * @param {Object} props.config - IPTC configuration object
 * @param {(config: Object) => void} props.onChange - Callback when config changes
 * @param {Object} props.matchData - Match form data from step 1
 * @param {Array} props.teams - Available teams for name lookup
 * @param {Function} props.fetchCompanyDefaults - API function to fetch company IPTC defaults
 * @param {Function} props.fetchPreview - API function to preview rendered templates
 */
export default function IptcConfigStep({
  config,
  onChange,
  matchData,
  teams,
  fetchCompanyDefaults,
  fetchPreview,
}) {
  const [preview, setPreview] = useState(null);
  const [loadingDefaults, setLoadingDefaults] = useState(false);
  const hasPrefilledRef = useRef(false);

  // Pre-fill IPTC fields from match data on first render (only if fields are empty)
  useEffect(() => {
    // Only pre-fill once per wizard session
    if (hasPrefilledRef.current) return;

    // Check if IPTC fields are already populated (don't overwrite user edits)
    const hasExistingData =
      (config.keywords && config.keywords.length > 0) ||
      config.headline_template ||
      config.caption_template ||
      config.event_name ||
      config.location;

    if (hasExistingData) {
      hasPrefilledRef.current = true;
      return;
    }

    // Generate pre-fill values from match data
    const prefillData = generateIptcFromMatchData(matchData, teams);
    if (!prefillData) {
      hasPrefilledRef.current = true;
      return;
    }

    // Apply pre-fill values
    const newConfig = { ...config };
    if (prefillData.title_template && !config.title_template) {
      newConfig.title_template = prefillData.title_template;
    }
    if (prefillData.keywords.length > 0 && (!config.keywords || config.keywords.length === 0)) {
      newConfig.keywords = prefillData.keywords;
    }
    if (prefillData.headline_template && !config.headline_template) {
      newConfig.headline_template = prefillData.headline_template;
    }
    if (prefillData.caption_template && !config.caption_template) {
      newConfig.caption_template = prefillData.caption_template;
    }
    if (prefillData.event_name && !config.event_name) {
      newConfig.event_name = prefillData.event_name;
    }
    if (prefillData.location && !config.location) {
      newConfig.location = prefillData.location;
    }
    if (prefillData.city && !config.city) {
      newConfig.city = prefillData.city;
    }
    if (prefillData.state && !config.state) {
      newConfig.state = prefillData.state;
    }
    if (prefillData.country && !config.country) {
      newConfig.country = prefillData.country;
    }
    if (prefillData.country_code && !config.country_code) {
      newConfig.country_code = prefillData.country_code;
    }
    if (prefillData.credit && !config.credit) {
      newConfig.credit = prefillData.credit;
    }
    if (prefillData.category && !config.category) {
      newConfig.category = prefillData.category;
    }
    if (prefillData.supplemental_categories?.length > 0
        && (!config.supplemental_categories || config.supplemental_categories.length === 0)) {
      newConfig.supplemental_categories = prefillData.supplemental_categories;
    }

    onChange(newConfig);
    hasPrefilledRef.current = true;
  }, [config, matchData, teams, onChange]);

  // Update a single field
  const updateField = useCallback((field, value) => {
    onChange({ ...config, [field]: value });
  }, [config, onChange]);

  // Load company defaults
  const handleLoadDefaults = async () => {
    try {
      setLoadingDefaults(true);
      const defaults = await fetchCompanyDefaults();
      onChange({
        ...config,
        title_template: defaults.title_template || config.title_template,
        headline_template: defaults.headline_template || config.headline_template,
        caption_template: defaults.caption_template || config.caption_template,
        credit: defaults.credit || config.credit,
        copyright_notice: defaults.copyright_notice || config.copyright_notice,
      });
    } catch (err) {
      console.error('Failed to load company defaults:', err);
    } finally {
      setLoadingDefaults(false);
    }
  };

  // Fetch preview when templates change - use real match data as context
  useEffect(() => {
    const templates = {
      title_template: config.title_template,
      headline_template: config.headline_template,
      caption_template: config.caption_template,
    };

    // Only fetch if at least one template has content
    const hasContent = Object.values(templates).some((t) => t?.trim());
    if (!hasContent) {
      setPreview(null);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        const context = buildPreviewContext(matchData, teams);
        const result = await fetchPreview(templates, context);
        setPreview(result.preview);
      } catch (err) {
        console.error('Preview fetch failed:', err);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [config.title_template, config.headline_template, config.caption_template, fetchPreview, matchData, teams]);

  return (
    <div className={styles.iptcConfigStep}>
      <div className={styles.stepHeader}>
        <p className={styles.stepDescription}>
          Configure IPTC metadata that will be embedded in exported photos.
          Use variables like {'{home_team}'} for dynamic content.
        </p>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleLoadDefaults}
          loading={loadingDefaults}
        >
          Load Company Defaults
        </Button>
      </div>

      <div className={styles.iptcColumns}>
        {/* Left column: Template fields */}
        <div className={styles.iptcFormColumn}>
          <div className={styles.formSection}>
            <h4 className={styles.sectionTitle}>Templates</h4>

            <TemplateInput
              label="Title / Object Name (dc:title)"
              value={config.title_template || ''}
              onChange={(v) => updateField('title_template', v)}
              placeholder="HOME TEAM_AWAY TEAM_CODE_DDMMYY"
            />

            <TemplateInput
              label="Headline (photoshop:Headline)"
              value={config.headline_template || ''}
              onChange={(v) => updateField('headline_template', v)}
              placeholder="{event} | {venue}"
            />

            <TemplateInput
              label="Caption (dc:description)"
              value={config.caption_template || ''}
              onChange={(v) => updateField('caption_template', v)}
              placeholder="{home_team} vs {away_team}, {competition}, {day} {monthname} {year4}"
              multiline
            />
          </div>

          <div className={styles.formSection}>
            <h4 className={styles.sectionTitle}>Location</h4>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>City</label>
                <input
                  type="text"
                  className={styles.input}
                  value={config.city || ''}
                  onChange={(e) => updateField('city', e.target.value)}
                  placeholder="Rotterdam"
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>State/Province</label>
                <input
                  type="text"
                  className={styles.input}
                  value={config.state || ''}
                  onChange={(e) => updateField('state', e.target.value)}
                  placeholder="Zuid-Holland"
                />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Country</label>
                <input
                  type="text"
                  className={styles.input}
                  value={config.country || ''}
                  onChange={(e) => updateField('country', e.target.value)}
                  placeholder="The Netherlands"
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Country Code</label>
                <input
                  type="text"
                  className={styles.input}
                  value={config.country_code || ''}
                  onChange={(e) => updateField('country_code', e.target.value)}
                  placeholder="NLD"
                  maxLength={3}
                />
              </div>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Sub-location (Venue)</label>
              <input
                type="text"
                className={styles.input}
                value={config.location || ''}
                onChange={(e) => updateField('location', e.target.value)}
                placeholder="De Kuip"
              />
            </div>
          </div>

          <div className={styles.formSection}>
            <h4 className={styles.sectionTitle}>Additional Metadata</h4>

            <div className={styles.formGroup}>
              <label className={styles.label}>Event Name</label>
              <input
                type="text"
                className={styles.input}
                value={config.event_name || ''}
                onChange={(e) => updateField('event_name', e.target.value)}
                placeholder="Eredivisie 2025-2026"
              />
            </div>

            <TagInput
              label="Keywords"
              value={config.keywords || []}
              onChange={(v) => updateField('keywords', v)}
              placeholder="Add keyword..."
            />

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Credit</label>
                <input
                  type="text"
                  className={styles.input}
                  value={config.credit || ''}
                  onChange={(e) => updateField('credit', e.target.value)}
                  placeholder="VK Sportphoto"
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Copyright Notice</label>
                <input
                  type="text"
                  className={styles.input}
                  value={config.copyright_notice || ''}
                  onChange={(e) => updateField('copyright_notice', e.target.value)}
                  placeholder="Copyright 2026 VK Sportphoto"
                />
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Category</label>
                <input
                  type="text"
                  className={styles.input}
                  value={config.category || ''}
                  onChange={(e) => updateField('category', e.target.value)}
                  placeholder="S"
                  maxLength={10}
                />
              </div>
              <div className={styles.formGroup}>
                <TagInput
                  label="Supplemental Categories"
                  value={config.supplemental_categories || []}
                  onChange={(v) => updateField('supplemental_categories', v)}
                  placeholder="Add category..."
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right column: Preview */}
        <div className={styles.iptcPreviewColumn}>
          <PreviewPanel preview={preview} config={config} />
        </div>
      </div>
    </div>
  );
}
